import type { HeroSection } from "@/content/types";

const SERVICE_CATEGORIES: HeroSection[] = [
  {
    key: "accounting",
    headline: "Trusted Accounting and Controller Services for",
    highlight: "Businesses Across Texas",
    description:
      "Orsak CPAs provides comprehensive accounting services for businesses and individuals in Houston, Texas. From financial statement preparation to cash flow management and internal controls, our accounting solutions deliver accuracy, efficiency, and clarity at every stage of growth.",
    tags: ["accounting"],
    publicId: "accounting",
    alt: "Orsak CPAs team members discussing accounting",
    caption:
      "Professional accounting and controller services from Orsak CPAs, serving businesses across Texas.",
  },
  {
    key: "advisory",
    headline: "Tailored Business Consulting and Advisory Services to",
    highlight: "Help You Grow with Confidence",
    description:
      "Orsak CPAs partners with Houston businesses and individuals to provide advisory services that turn financial data into strategy. From budgeting and succession planning to software implementation and process improvement, we help clients make informed decisions with lasting impact.",
    tags: ["advisory"],
    publicId: "advisory",
    alt: "Orsak CPAs advisor meeting with a Houston business owner to discuss strategy",
    caption:
      "Tailored business consulting and advisory services to help Texas companies grow with confidence.",
  },
  {
    key: "audit",
    headline: "Comprehensive Audit, Review, and Reporting Services for",
    highlight: "Businesses, Organizations, and Individuals",
    description:
      "Orsak CPAs offers audit and assurance services designed to strengthen trust and compliance. Serving businesses and organizations across Houston, Texas, we provide financial statement audits, reviews, and compilations that bring confidence and transparency to your reporting.",
    tags: ["audit"],
    publicId: "audit",
    alt: "Orsak CPAs auditor reviewing financial statements with a client",
    caption:
      "Independent audit, review, and assurance services from Orsak CPAs, bringing trust and transparency.",
  },
  {
    key: "business",
    headline: "Expert CPA Services for",
    highlight: "Businesses of All Sizes and Stages",
    description:
      "Orsak CPAs supports Houston businesses with outsourced CFO services, accounting system setup, policy development, and financial oversight. Our business services are built to improve efficiency, ensure compliance, and position your company for sustainable growth.",
    tags: ["business"],
    publicId: "business",
    alt: "CPA providing outsourced CFO and business operations support",
    caption:
      "Comprehensive CPA services supporting businesses of all sizes and stages across Texas.",
  },
  {
    key: "individual",
    headline: "Personalized CPA Services for",
    highlight: "High-Income Individuals, Ranch Owners, and Oil Professionals",
    description:
      "Orsak CPAs provides personalized accounting and tax services for individuals and families in Houston, Texas. From estate and trust planning to personal financial statements and federal tax returns, we offer solutions that protect wealth and bring peace of mind.",
    tags: ["individual"],
    publicId: "individual",
    alt: "Advisor helping an individual client with tax and estate planning",
    caption:
      "Personalized accounting and tax services for high-income individuals, ranch owners, and oil professionals.",
  },
  {
    key: "tax",
    headline: "Tax Planning and Preparation Services for",
    highlight: "Businesses and Individuals",
    description:
      "Orsak CPAs delivers tailored tax services for businesses and individuals in Houston, Texas. We handle complex returns, multistate filings, and proactive tax planning strategies that minimize liability and keep you compliant with confidence.",
    tags: ["tax"],
    publicId: "tax",
    alt: "Tax professional preparing and reviewing returns at Orsak CPAs",
    caption:
      "Expert tax planning and preparation for businesses and individuals in Houston, Texas.",
  },
];

const SERVICE_ITEMS: HeroSection[] = [
  {
    key: "accounting-system-selection-and-setup",
    headline: "Trusted Accounting System Selection & Setup for",
    highlight: "Businesses and Organizations",
    description:
      "Orsak CPAs helps Houston, Texas organizations choose, configure, and migrate to the right accounting software—streamlining workflows, improving accuracy, and setting a scalable financial foundation from day one.",
    tags: ["accounting", "advisory", "business"],
    publicId: "accounting-system-selection-and-setup",
    alt: "CPA configuring accounting software during an implementation meeting",
    caption:
      "Accounting system selection, migration, and setup to streamline workflows and accuracy for Houston organizations.",
  },
  {
    key: "compilation-of-financial-statements",
    headline: "Compilation of Financial Statements for",
    highlight: "Private Businesses and Individuals",
    description:
      "We compile financial statements from client-provided information to present a clear picture of performance—supporting lenders, stakeholders, and planning needs while maintaining GAAP presentation where applicable.",
    tags: ["accounting", "business"],
    publicId: "compilation-of-financial-statements",
    alt: "CPA compiling client-provided data into financial statements",
    caption:
      "Clear, GAAP-presented compilations that support lenders, stakeholders, and planning needs.",
  },
  {
    key: "financial-position-job-descriptions",
    headline: "Financial Position Job Descriptions & Role Clarity for",
    highlight: "Businesses and Nonprofits",
    description:
      "Define responsibilities, controls, and reporting lines with role-specific job descriptions that strengthen your finance team, reduce risk, and support scalable operations.",
    tags: ["accounting", "advisory", "business"],
    publicId: "financial-position-job-descriptions",
    alt: "Finance team reviewing role descriptions and responsibilities",
    caption:
      "Role clarity and job descriptions that tighten controls and enable scalable finance operations.",
  },
  {
    key: "policies-and-procedures",
    headline: "Financial Policies & Procedures Development for",
    highlight: "Businesses and Nonprofits",
    description:
      "We design practical, enforceable financial policies and procedures—improving internal controls, standardizing processes, and ensuring compliance across your organization.",
    tags: ["accounting", "advisory", "business", "audit"],
    publicId: "policies-and-procedures",
    alt: "Advisor drafting financial policies and procedures with a client",
    caption:
      "Practical, enforceable policies and procedures that standardize processes and improve compliance.",
  },
  {
    key: "preparation-of-financial-statements",
    headline: "Preparation of Financial Statements for",
    highlight:
      "Businesses, Nonprofits, and Individuals in Texas and the Gulf Coast",
    description:
      "Orsak CPAs prepares timely, well-organized financial statements to support management, lenders, and auditors—delivering accuracy and clarity for confident decision-making.",
    tags: ["accounting", "business", "individual"],
    publicId: "preparation-of-financial-statements",
    alt: "Accountant preparing monthly financial statements for management review",
    caption:
      "Timely, well-organized financial statements that support decision-making, lending, and audits.",
  },
  {
    key: "software-selection-and-implementation",
    headline: "Accounting Software Selection & Implementation for",
    highlight: "Growing Businesses in Texas and the Gulf Coast Region",
    description:
      "From evaluation to go-live, we implement modern accounting stacks—integrations, chart of accounts, and workflows—so your team gains efficiency, visibility, and reliable data.",
    tags: ["accounting", "advisory", "business"],
    publicId: "software-selection-and-implementation",
    alt: "Team implementing modern accounting software and integrations",
    caption:
      "End-to-end software selection and implementation for efficient workflows and reliable financial data.",
  },
  {
    key: "quickbooks-services",
    headline: "QuickBooks Setup, Training, and Support for",
    highlight: "Houston Businesses and Growing Teams",
    description:
      "Orsak CPAs helps Houston businesses get QuickBooks right—setup and configuration of QuickBooks Online or Desktop, file cleanup, and hands-on training so your books stay accurate and your reporting stays reliable.",
    tags: ["accounting", "business"],
    publicId: "software-selection-and-implementation",
    alt: "Accountant configuring QuickBooks and training a business team",
    caption:
      "QuickBooks setup, training, and ongoing support for accurate books and confident reporting.",
  },
  {
    key: "budgeting-and-forecasts",
    headline: "Budgeting & Financial Forecasting Services for",
    highlight: "Businesses and Organizations",
    description:
      "Build realistic budgets and rolling forecasts that align with strategy, cash needs, and market conditions—turning your numbers into an actionable plan for growth.",
    tags: ["advisory", "accounting", "business", "individual"],
    publicId: "budgeting-and-forecasts",
    alt: "CFO reviewing a budget and rolling forecast dashboard",
    caption:
      "Realistic budgets and rolling forecasts aligned to strategy, cash needs, and growth.",
  },
  {
    key: "cash-flow-management",
    headline: "Cash Flow Management Services for",
    highlight: "Businesses, Nonprofits, and Individuals",
    description:
      "We help you model inflows/outflows, optimize working capital, and plan for contingencies—so cash is available when you need it and surprises are minimized.",
    tags: ["advisory", "business", "individual", "accounting"],
    publicId: "cash-flow-management",
    alt: "Controller analyzing cash inflows, outflows, and working capital",
    caption:
      "Cash flow modeling and working-capital planning to keep operations running smoothly and predictably.",
  },
  {
    key: "disclosures-and-budget-support",
    headline: "Disclosures & Budget Support for",
    highlight: "Financial Reporting and Grants",
    description:
      "Prepare accurate disclosures, supporting schedules, and grant budgets that satisfy reporting requirements and make audits and reviews more efficient.",
    tags: ["advisory", "accounting", "audit", "business"],
    publicId: "disclosures-and-budget-support",
    alt: "CPA preparing financial disclosures and grant budget schedules",
    caption:
      "Accurate disclosures and supporting schedules for efficient reporting, grants, audits, and reviews.",
  },
  {
    key: "gaap-implementation",
    headline: "GAAP Implementation Services for",
    highlight: "Businesses and Nonprofits",
    description:
      "Orsak CPAs guides GAAP adoption and improvements—revamping policies, chart structures, and recognition rules to elevate compliance and reporting quality.",
    tags: ["advisory", "accounting", "audit", "business"],
    publicId: "gaap-implementation",
    alt: "Advisor aligning accounting policies and chart of accounts with GAAP",
    caption:
      "Hands-on GAAP adoption and improvements that elevate compliance and reporting quality.",
  },

  // 11–20
  {
    key: "medicare-cost-reports",
    headline: "Medicare Cost Report Preparation for",
    highlight: "Hospices and Healthcare Providers",
    description:
      "We prepare compliant Medicare cost reports for hospices and providers—supporting accurate reimbursement, defensible allocations, and timely submission.",
    tags: ["advisory", "business", "accounting"],
    publicId: "medicare-cost-reports",
    alt: "CPA preparing a Medicare cost report for a hospice provider",
    caption:
      "Compliant Medicare cost report preparation to support accurate reimbursement and timely filing.",
  },
  {
    key: "supplemental-schedules",
    headline: "Supplemental Schedules to Support",
    highlight: "Accurate Financial Reporting and Audits",
    description:
      "Create clear, audit-ready supplemental schedules that reconcile to your books and substantiate disclosures, grants, and management reporting.",
    tags: ["advisory", "accounting", "audit", "business"],
    publicId: "supplemental-schedules",
    alt: "Accountant assembling audit-ready supplemental schedules and reconciliations",
    caption:
      "Audit-ready supplemental schedules that reconcile cleanly to your books and disclosures.",
  },
  {
    key: "succession-planning-and-wealth-transfer",
    headline: "Succession Planning & Wealth Transfer for",
    highlight: "High-Net-Worth Individuals and Families",
    description:
      "Protect and transition wealth with coordinated tax, estate, and business-succession strategies—tailored for long-term family goals and continuity.",
    tags: ["advisory", "tax", "individual", "business"],
    publicId: "succession-planning-and-wealth-transfer",
    alt: "Advisor discussing succession and wealth transfer strategies with a family business",
    caption:
      "Coordinated tax, estate, and succession strategies to protect and transition family wealth.",
  },
  {
    key: "audit-of-financial-statements",
    headline: "Financial Statement Audits for",
    highlight: "Businesses, Nonprofits, and Individuals",
    description:
      "Independent audits that enhance trust, strengthen controls, and meet compliance—delivered with clear communication and practical recommendations.",
    tags: ["audit", "accounting", "business"],
    publicId: "audit-of-financial-statements",
    alt: "Auditor conducting a financial statement audit and walkthroughs",
    caption:
      "Independent audits that build trust, strengthen controls, and meet compliance requirements.",
  },
  {
    key: "audits-reviews-and-financial-statements",
    headline: "Audits, Reviews, and Financial Statement Services for",
    highlight: "Businesses and Organizations",
    description:
      "Whether you need an audit, review, or compilation, Orsak CPAs provides the right level of assurance to satisfy lenders, boards, and regulatory requirements.",
    tags: ["audit", "accounting", "business"],
    publicId: "audits-reviews-and-financial-statements",
    alt: "CPA team performing audit, review, and compilation assurance services",
    caption:
      "Right-sized assurance—audits, reviews, or compilations—to satisfy lenders, boards, and regulators.",
  },
  {
    key: "internal-control-documentation",
    headline: "Internal Control Documentation Services for",
    highlight: "Businesses and Nonprofits",
    description:
      "We assess, document, and refine internal controls—reducing risk, improving reliability, and preparing you for audits and growth.",
    tags: ["audit", "accounting", "advisory", "business"],
    publicId: "internal-control-documentation",
    alt: "CPA documenting internal controls and process narratives",
    caption:
      "Clear, tested internal control documentation that reduces risk and supports audit readiness.",
  },
  {
    key: "internal-controls",
    headline: "Internal Controls Consulting for",
    highlight: "Businesses and Nonprofits",
    description:
      "Design and implement practical control frameworks that safeguard assets, prevent errors, and support confident financial reporting.",
    tags: ["audit", "advisory", "accounting", "business"],
    publicId: "internal-controls",
    alt: "Consultant designing an internal control framework with management",
    caption:
      "Practical control frameworks that safeguard assets and support reliable financial reporting.",
  },
  {
    key: "review-of-financial-statements",
    headline: "Review of Financial Statements for",
    highlight: "Private Companies and Nonprofits",
    description:
      "A CPA review provides limited assurance through analytical procedures and inquiries—elevating credibility without the scope of a full audit.",
    tags: ["audit", "accounting", "business"],
    publicId: "review-of-financial-statements",
    alt: "CPA reviewing financial statements to provide limited assurance",
    caption:
      "Limited assurance via CPA reviews—analytics and inquiries that elevate credibility.",
  },
  {
    key: "special-purpose-financial-statements",
    headline: "Special Purpose Financial Statements for",
    highlight: "Planning, Projections, and Presentations",
    description:
      "We prepare tailored financial statements—projections, cash-basis, tax-basis, or other special-purpose frameworks—to meet specific stakeholder needs.",
    tags: ["audit", "accounting", "advisory", "business"],
    publicId: "special-purpose-financial-statements",
    alt: "Accountant preparing special-purpose financial statements and projections",
    caption:
      "Projections, cash-basis, tax-basis, and other special-purpose statements for specific needs.",
  },
  {
    key: "part-time-controller-cfo",
    headline: "Part-Time Controller & Fractional CFO Services for",
    highlight: "Growing Organizations",
    description:
      "Access senior-level finance leadership—forecasting, KPIs, cash planning, and board-ready reporting—without the full-time cost.",
    tags: ["business", "advisory", "accounting"],
    publicId: "part-time-controller-cfo",
    alt: "Fractional CFO reviewing KPIs and forecasts with leadership",
    caption:
      "Fractional controller/CFO support—forecasting, KPIs, cash planning, and board-ready reporting.",
  },

  // 21-30
  {
    key: "debt-covenant-compliance",
    headline: "Debt Covenant Compliance Support for",
    highlight: "Lenders, Borrowers, and Businesses",
    description:
      "Stay in compliance with timely calculations, tracking, and lender-ready packages—reducing risk and maintaining credit relationships.",
    tags: ["business", "advisory", "accounting"],
    publicId: "debt-covenant-compliance",
    alt: "Controller preparing debt covenant calculations for a lender package",
    caption:
      "Timely covenant calculations and reporting to reduce risk and maintain lender relationships.",
  },
  {
    key: "development-of-budgets",
    headline: "Development of Budgets for",
    highlight: "Businesses, Nonprofits, and Individuals",
    description:
      "We build practical, measurable budgets that align resources with goals—creating accountability and visibility across departments and programs.",
    tags: ["business", "advisory", "accounting"],
    publicId: "development-of-budgets",
    alt: "Finance lead reviewing department budgets and allocations",
    caption:
      "Practical, measurable budgets that align resources with organizational goals.",
  },
  {
    key: "development-of-financial-policies-and-procedures",
    headline: "Financial Policies & Procedures Development for",
    highlight: "Businesses and Nonprofits",
    description:
      "Codify best practices with clear, implementable policies and procedures that tighten controls and standardize your finance operations.",
    tags: ["business", "accounting", "advisory", "audit"],
    publicId: "development-of-financial-policies-and-procedures",
    alt: "Advisor drafting finance policies and procedures with leadership",
    caption:
      "Clear, implementable policies and procedures that standardize processes and tighten controls.",
  },
  {
    key: "successful-planning",
    headline: "Strategic Financial Planning for",
    highlight: "Long-Term Business and Personal Success",
    description:
      "Translate strategy into numbers with actionable financial plans—capital needs, scenario modeling, and milestones that keep you on track.",
    tags: ["business", "advisory", "individual", "tax"],
    publicId: "successful-planning",
    alt: "Executive team reviewing a strategic financial plan with scenario models",
    caption:
      "Actionable financial plans—capital planning, scenarios, and milestones for long-term success.",
  },
  {
    key: "individual-accounting",
    headline: "Individual Accounting Services for",
    highlight: "High-Income Professionals and Families",
    description:
      "Personalized accounting for complex lives—investment activity, K-1s, rentals, and planning—so your finances stay organized and opportunity-ready.",
    tags: ["individual", "accounting", "tax"],
    publicId: "individual-accounting",
    alt: "Advisor organizing individual accounting records and investment activity",
    caption:
      "Personalized accounting for high-income professionals and families with complex finances.",
  },
  {
    key: "personal-financial-statements",
    headline: "Personal Financial Statement Preparation for",
    highlight: "High-Net-Worth Individuals",
    description:
      "We prepare lender-ready and planning-ready personal financial statements that clearly present assets, liabilities, and net worth.",
    tags: ["individual", "accounting", "advisory", "tax"],
    publicId: "personal-financial-statements",
    alt: "CPA preparing a personal financial statement for a high-net-worth client",
    caption:
      "Clear, lender-ready personal financial statements for planning and credit needs.",
  },
  {
    key: "estate-and-trusts",
    headline: "Estate & Trust Tax and Reporting Services for",
    highlight: "Trustees, Executors, and Beneficiaries",
    description:
      "Orsak CPAs handles fiduciary returns, distributions, and tax strategy for estates and trusts—coordinating with attorneys and advisors to protect beneficiaries.",
    tags: ["tax", "advisory", "individual", "business"],
    publicId: "estate-and-trusts",
    alt: "Advisor coordinating estate and trust tax filings and distributions",
    caption:
      "Fiduciary returns and coordinated strategies to protect beneficiaries and comply with trust requirements.",
  },
  {
    key: "federal-tax-return-preparation",
    headline: "Federal Tax Return Preparation for",
    highlight: "Individuals and Businesses",
    description:
      "From complex individual filings to corporate and partnership returns, we prepare accurate federal tax returns and identify opportunities to reduce liability.",
    tags: ["tax", "individual", "business"],
    publicId: "federal-tax-return-preparation",
    alt: "Tax professional preparing federal returns for a business and individual",
    caption:
      "Accurate federal tax preparation for individuals, corporations, and partnerships.",
  },
  {
    key: "state-franchise-income-and-tax-return",
    headline: "State Franchise & Income Tax Return Preparation for",
    highlight: "Businesses",
    description:
      "We manage multistate franchise and income tax returns—nexus evaluation, apportionment, and filings—to keep you compliant across jurisdictions.",
    tags: ["tax", "business", "advisory"],
    publicId: "state-franchise-income-and-tax-return",
    alt: "CPA reviewing multistate franchise and income tax apportionment",
    caption:
      "Multistate franchise and income tax compliance—nexus, apportionment, and filings.",
  },
  {
    key: "tax-compliance",
    headline: "Tax Compliance Services for",
    highlight: "Individuals, Businesses, and Estates in Texas",
    description:
      "Comprehensive federal and state tax compliance—accurate filings, timely extensions, and year-round support that minimizes surprises.",
    tags: ["tax", "business", "individual"],
    publicId: "tax-compliance",
    alt: "Tax team preparing compliant federal and state filings",
    caption:
      "Year-round federal and state tax compliance with accurate filings and timely extensions.",
  },

  // 31
  {
    key: "tax-planning",
    headline: "Strategic Tax Planning for",
    highlight: "Businesses, Individuals, and High-Income Clients",
    description:
      "Proactive, data-driven tax strategies tailored to your goals—entity structure, timing, deductions, credits, and wealth-transfer opportunities.",
    tags: ["tax", "advisory", "individual", "business"],
    publicId: "tax-planning",
    alt: "Tax advisor reviewing entity structure, timing, and deduction strategies with a client",
    caption:
      "Proactive, data-driven tax planning—entity choice, timing, deductions, credits, and wealth transfer.",
  },
  {
    key: "oil-and-gas-royalty-tax",
    headline: "Oil & Gas Royalty and Mineral Owner Tax for",
    highlight: "Texas Owners and Families",
    description:
      "Royalty checks and division orders bring tax rules most preparers rarely touch. We handle depletion, intangible drilling costs, and working vs. royalty interest so you keep more of what your minerals earn.",
    tags: ["tax", "individual", "business", "advisory"],
    publicId: "oil-and-gas",
    alt: "CPA reviewing oil and gas royalty statements and depletion schedules with a mineral owner",
    caption:
      "Depletion, IDCs, and interest-type analysis for royalty and mineral owners across Texas.",
  },
  {
    key: "ranch-and-landowner-tax",
    headline: "Ranch, Farm, and Landowner Tax for",
    highlight: "Texas Land and Mineral Owners",
    description:
      "Texas land carries its own tax playbook—agricultural and wildlife valuation, rollback exposure, mineral and royalty income, and land 1031 exchanges. We help owners protect value across generations.",
    tags: ["tax", "individual", "business", "advisory"],
    publicId: "ranching-and-agribusiness",
    alt: "CPA discussing agricultural valuation and land tax strategy with a Texas ranch owner",
    caption:
      "Ag and wildlife valuation, mineral income, conservation easements, and multi-generational transfer.",
  },
  {
    key: "real-estate-investor-tax",
    headline: "Real Estate Investor Tax and 1031 Strategy for",
    highlight: "Houston Investors and Operators",
    description:
      "Rentals, syndications, and flips reward investors who plan ahead. We coordinate 1031 exchanges, cost segregation, and real estate professional status to lower your effective tax rate.",
    tags: ["tax", "individual", "business", "advisory"],
    publicId: "real-estate",
    alt: "CPA reviewing a cost segregation study and 1031 exchange timeline with a real estate investor",
    caption:
      "1031 exchanges, cost segregation, REP status, and passive-activity planning for investors.",
  },
  {
    key: "business-sale-exit",
    headline: "Business Sale and Exit Tax Planning for",
    highlight: "Owners Preparing to Sell",
    description:
      "How you structure a sale can swing your after-tax proceeds dramatically. We plan QSBS, installment sales, and asset-versus-stock structuring well before the letter of intent—so the deal works in your favor.",
    tags: ["tax", "business", "advisory", "individual"],
    publicId: "successful-planning",
    alt: "CPA advising a business owner on deal structuring and after-tax proceeds before a sale",
    caption:
      "QSBS, installment sales, allocation, and pre-sale planning to maximize after-tax proceeds.",
  },
];

const INDUSTRIES: HeroSection[] = [
  {
    key: "construction",
    headline: "Trusted CPA Services for",
    highlight: "Construction Companies and Contractors",
    description:
      "Orsak CPAs provides accounting and advisory services to construction companies across Houston, Texas. From project-based cost tracking to bonding and compliance, we deliver financial oversight and strategy to help builders, contractors, and developers succeed.",
    tags: [],
    publicId: "construction",
    alt: "Contractor and CPA reviewing construction project budgets and timelines",
    caption:
      "Specialized accounting, cost tracking, and compliance support for Texas construction companies and contractors.",
  },
  {
    key: "manufacturing",
    headline: "Comprehensive CPA Services for",
    highlight: "Texas-Based Producers and Manufacturers",
    description:
      "Orsak CPAs supports manufacturers in Houston, Texas with accounting, tax, and advisory services designed to keep operations efficient and compliant. Our team helps streamline financial systems, manage costs, and optimize growth strategies for businesses in the manufacturing sector.",
    tags: [],
    publicId: "manufacturing",
    alt: "Operations manager inspecting a production line and inventory controls",
    caption:
      "Accounting, tax, and cost-management strategies built for manufacturers and producers across Texas.",
  },
  {
    key: "real-estate",
    headline: "Expert CPA Services for",
    highlight: "Real Estate Investors, Developers, and Property Managers",
    description:
      "Orsak CPAs delivers specialized accounting and tax solutions for real estate investors, brokers, and developers in Houston, Texas. We help clients navigate transactions, manage property portfolios, and optimize tax positions with confidence and clarity.",
    tags: [],
    publicId: "real-estate",
    alt: "Real estate developer reviewing property financials and project timelines",
    caption:
      "Transaction support, portfolio accounting, and tax optimization for investors, developers, and managers.",
  },
  {
    key: "technology",
    headline: "Tailored CPA Services for",
    highlight: "Technology Startups and Growth Companies",
    description:
      "Orsak CPAs partners with Houston technology companies to provide financial strategies that keep pace with innovation. From startup growth and funding to compliance and tax planning, we offer CPA services that help tech businesses scale with stability.",
    tags: [],
    publicId: "technology",
    alt: "Startup team collaborating on product roadmaps and funding plans",
    caption:
      "Startup-ready accounting, runway planning, and compliance for Houston technology companies.",
  },
  {
    key: "transportation",
    headline: "Trusted CPA Services for",
    highlight: "Transportation, Logistics, and Fleet-Based Businesses",
    description:
      "Orsak CPAs works with transportation and logistics companies in Houston, Texas to manage complex accounting, tax, and compliance needs. We provide financial visibility and strategic planning to help keep operations moving smoothly and profitably.",
    tags: [],
    publicId: "transportation",
    alt: "Logistics coordinator monitoring fleet routes and delivery schedules",
    caption:
      "Fleet-focused accounting, tax, and compliance services for transportation and logistics businesses.",
  },
  {
    key: "oil-and-gas",
    headline: "Specialized CPA Services for",
    highlight: "Oil & Gas Companies, Royalty Owners, and Energy Investors",
    description:
      "Orsak CPAs serves oil and gas companies across Houston and Texas with specialized accounting, tax, and advisory services. Our expertise helps energy businesses navigate compliance, manage revenue streams, and plan strategically for growth in a competitive industry.",
    tags: [],
    publicId: "oil-and-gas",
    alt: "Energy professional reviewing oil and gas production reports on-site",
    caption:
      "Revenue accounting, compliance, and tax planning for oil & gas operators, royalty owners, and investors.",
  },
  {
    key: "healthcare",
    headline: "Comprehensive CPA Services for",
    highlight: "Medical Practices, Clinics, and Healthcare Providers",
    description:
      "Orsak CPAs provides accounting and tax services tailored to healthcare providers in Houston, Texas. We support doctors, clinics, and medical organizations with compliance, cost management, and financial strategies that strengthen operations and improve outcomes.",
    tags: [],
    publicId: "healthcare",
    alt: "Clinic administrator reviewing practice financials and scheduling metrics",
    caption:
      "Practice-focused accounting, reimbursement guidance, and tax services for healthcare providers.",
  },
  {
    key: "ranching-and-agribusiness",
    headline: "Trusted CPA Services for",
    highlight: "Ranchers, Farmers, and Agribusinesses",
    description:
      "Orsak CPAs partners with Texas ranchers and agribusinesses to deliver financial clarity and tax solutions designed for agriculture. From land management to succession planning, we help agricultural operations thrive with dependable accounting and advisory support.",
    tags: [],
    publicId: "ranching-and-agribusiness",
    alt: "Rancher reviewing livestock and land management expenses",
    caption:
      "Agriculture-aware accounting, tax, and succession planning for ranching and agribusiness operations.",
  },
  {
    key: "professional",
    headline: "Tailored CPA Services for",
    highlight: "Attorneys, Consultants, Agencies, and Professional Firms",
    description:
      "Orsak CPAs supports professional service firms in Houston, Texas—including law, consulting, and design practices—with precise accounting, tax, and advisory services. We help service businesses strengthen financial systems, reduce tax burdens, and focus on growth.",
    tags: [],
    publicId: "professional",
    alt: "Professional services team meeting to review budgets and billing",
    caption:
      "Purpose-built accounting and tax strategies for law firms, consultants, agencies, and other professional practices.",
  },
  {
    key: "nonprofits",
    headline: "Expert CPA Services for",
    highlight: "Nonprofits, Foundations, and Faith-Based Organizations",
    description:
      "Orsak CPAs helps nonprofit organizations in Houston, Texas stay financially healthy and compliant. We provide accounting, audit, and tax services that support transparency, strengthen donor confidence, and free leaders to focus on their mission.",
    tags: [],
    publicId: "nonprofits",
    alt: "Nonprofit board reviewing budgets and grant reports",
    caption:
      "Transparent accounting, audit, and tax support for nonprofits, foundations, and faith-based organizations.",
  },
];

//
// Locations
//
const LOCATIONS: HeroSection[] = [
  {
    key: "locations",
    headline:
      "Serving Clients Across Houston, Texas, and the Gulf Coast Region.",
    highlight: "Trusted CPA Expertise Where You Do Business",
    description:
      "Orsak CPA's provides comprehensive accounting, tax, and advisory services for individuals, businesses, and organizations across Houston, Texas, and the broader Gulf Coast corridor-from Corpus Christi to New Orleans. Whether you're headquartered locally or operate throughout the region, our experienced CPAs deliver the same clarity, compliance, and confidence that define our firm.",
    tags: [],
    publicId: "gulf-coast",
    alt: "",
  },
];

export const HERO_SECTIONS: HeroSection[] = [
  {
    key: "",
    headline: "Where Texas Business Meets",
    highlight: "Trusted CPAs",
    description:
      "A 21st-century CPA firm with deep Texas roots. From ranch gates to boardrooms, oilfields to family trusts — we bring modern financial strategy to the people and industries that built Texas.",
    tags: [],
    publicId:
      "https://res.cloudinary.com/diqsnjppi/image/upload/v1756927925/downtown-houston-texas.webp",
    alt: "Discover the vibrant Houston, Texas downtown skyline - a symbol of the city's thriving business and financial community. Featured on the Orsak CPAs landing page, this hero image reflects our commitment to serving clients across Houston and beyond with trusted accounting, audit, tax, and advisory services.",
  },
  {
    key: "services",
    headline: "Comprehensive CPA Services for ",
    highlight: "Individuals, Businesses, & Organizatons",
    description:
      "Orsak CPAs offers a full range of accounting, tax, and advisory services to businesses and individuals in Houston, Texas. From audits and financial reporting to tax planning, controller support, and strategic consulting, our services are built to reduce risk, improve efficiency, and create long-term financial confidence.",
    tags: ["accounting", "advisory", "audit", "business", "individual", "tax"],
    publicId: "services",
    alt: "CPA team collaborating on audits, tax planning, and accounting services",
  },
  {
    key: "industries",
    headline: "Serving Businesses Across",
    highlight: "All Industries",
    description:
      "Orsak CPAs partners with businesses and organizations across Houston, Texas and beyond, offering specialized CPA services tailored to the unique needs of each industry. From manufacturing and healthcare to real estate and professional services, we deliver financial clarity, compliance, and strategic insight that drive long-term success.",
    tags: [],
    publicId: "industries",
    alt: "Orsak CPAs advisor meeting with clients from construction, healthcare, real estate, and manufacturing",
  },
  {
    key: "contact-us",
    headline: "Get in Touch with Your Trusted CPAs in",
    highlight: "Houston, Texas",
    description:
      "Contact Orsak CPAs in Houston, Texas today. Our trusted team is here to answer your questions and provide expert accounting and tax guidance.",
    tags: [],
    publicId: "contact-orsak-cpas",
    alt: "CPA responding to a client inquiry by phone and email",
  },
  {
    key: "about",
    headline: "Trusted Houston CPAs Built on",
    highlight: "Service, Integrity, and Expertise",
    description:
      "Get to know Orsak Langner & Barthel PLLC—a Houston, Texas CPA firm serving businesses and individuals with accounting, tax, audit, and advisory expertise grounded in long-term client relationships.",
    tags: [],
    publicId: "downtown-houston-texas",
    alt: "Downtown Houston, Texas skyline representing Orsak CPAs' service area",
  },
  {
    key: "privacy",
    headline: "Privacy Policy",
    highlight: "Your Information, Protected",
    description:
      "How Orsak Langner & Barthel PLLC collects, uses, and safeguards the information you share with our Houston, Texas CPA firm.",
    tags: [],
    publicId: "contact-orsak-cpas",
    alt: "Orsak CPAs office representing client confidentiality and data protection",
  },
  {
    key: "irs-resolution",
    headline: "IRS Tax Resolution and Representation for",
    highlight: "Houston Businesses and Individuals",
    description:
      "Behind on taxes or facing IRS action? Orsak CPAs helps Houston, Texas taxpayers resolve back taxes, liens, levies, wage garnishment, and more—and deal with the IRS on your behalf.",
    tags: [],
    publicId: "tax",
    alt: "CPA helping a client resolve an IRS tax matter in Houston, Texas",
  },
  ...SERVICE_CATEGORIES,
  ...SERVICE_ITEMS,
  ...INDUSTRIES,
  ...LOCATIONS,
];

export const HERO_SECTIONS_BY_KEY = Object.fromEntries(
  HERO_SECTIONS.map(section => [section.key, section]),
);
